import React, { useEffect, useMemo, useState } from 'react';
import Box from 'Components/Atoms/Box';
import Typography from 'Components/Atoms/Typography';
import Button from 'Components/Atoms/Button';
import {
  Add,
  Delete,
  Edit,
  ArrowUpward,
  ArrowDownward,
} from 'Components/Atoms/Icons';
import { Event, EventAddOn, EventOption } from 'types/calendar';
import GTable, {
  ContextAction,
  ListConfigHeader,
} from 'Components/Organisms/GTable';
import EditOptionModal from './EditOptionModal';
import EditAddonModal from './EditAddonModal';
import RadioButtonGroup from 'Components/Atoms/RadioButtonGroup';
import { vatRateLabel } from 'types/shared/helper/eventVatComponents';
import './event-diner.scss';
import DescriptionPreview from './DescriptionPreview';

/** Rates for the table: "19 %", "7 % + 19 %", "2,6 % + 8,1 %" (the column does not wrap). */
const vatColumnText = (option: { vatRate?: string; vatComponents?: { vatRate: string }[] | null }) =>
  (option.vatComponents
    ? Array.from(new Set(option.vatComponents.map((c) => c.vatRate)))
    : [option.vatRate || '0.00']
  )
    .map((rate) => vatRateLabel(rate))
    .join(' + ');

/** Price cell: a free option shows 0,00 € like every other amount. */
const priceText = (amount?: number | null) => `${(amount ?? 0).toFixed(2).replace('.', ',')} €`;

// The table's action column is pinned to the right edge (GTable); the VAT
// column gets its own width and never wraps, so the rates stay readable
// next to the actions instead of underneath them.
const VAT_COLUMN = { width: 104, style: { whiteSpace: 'nowrap' as const } };

type EventDinerProps = {
  options?: EventOption[];
  optionalAddOns?: EventAddOn[];
  setEventState: React.Dispatch<React.SetStateAction<Event | null>>;
  eventState: Event | null;
};

const EventDiner = ({
  options,
  optionalAddOns,
  setEventState,
  eventState,
}: EventDinerProps) => {
  const [openEditOptions, setOpenEditOptions] = useState<
    | { isOpen: boolean; optId: string | null; type: 'options' | 'addons' }
    | false
  >(false);

  // OPTIONS HEADER
  const headersOptions: ListConfigHeader<EventOption>[] = [
    {
      field: 'image',
      headerName: 'Image',
      headerTranslation: 'common',
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon' && data.img) {
          return (
            <img
              alt=""
              style={{
                width: 48,
                height: 48,
                objectFit: 'cover',
              }}
              src={data.img}
            />
          );
        }
      },
    },
    {
      field: 'title',
      headerName: 'Title: DE / EN',
      width: 140,
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon') {
          return (
            <Typography color="subdued" variant="text-3">
              {data.translation.de.title}
            </Typography>
          );
        }
      },
    },
    {
      field: 'description',
      width: 180,
      headerName: 'Description',
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon') {
          return (
            <DescriptionPreview description={data.translation.de.description} />
          );
        }
      },
    },
    {
      field: 'amount',
      headerName: 'Price',
      style: { whiteSpace: 'nowrap' },
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon') {
          return (
            <Typography color="subdued" variant="text-3">
              {priceText(data.amount)}
            </Typography>
          );
        }
      },
    },
    {
      field: 'limited',
      headerName: 'Limited',
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon') {
          return (
            <Typography color="subdued" variant="text-3">
              {data.limited || '∞'}
            </Typography>
          );
        }
      },
    },
    {
      field: 'wholeGroupOnly',
      headerName: 'Selection',
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon') {
          return (
            <Typography color="subdued" variant="text-3">
              {data.wholeGroupOnly ? 'Whole group' : '-'}
            </Typography>
          );
        }
      },
    },
    {
      field: 'vatRate',
      headerName: 'VAT',
      ...VAT_COLUMN,
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon') {
          return (
            <Typography color="subdued" variant="text-3">
              {vatColumnText(data)}
            </Typography>
          );
        }
      },
    },
  ];

  // ADDONS HEADER
  const headersOptionalAddons: ListConfigHeader<EventAddOn>[] = [
    {
      field: 'image',
      headerName: 'Image',
      headerTranslation: 'common',
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon' && data.img) {
          return (
            <img
              alt=""
              style={{ width: 48, height: 48, objectFit: 'cover' }}
              src={data.img}
            />
          );
        }
      },
    },
    {
      field: 'title',
      headerName: 'Title: DE / EN',
      width: 140,
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon') {
          return (
            <Typography color="subdued" variant="text-3">
              {data.translation.de.title}
            </Typography>
          );
        }
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 180,
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon') {
          return (
            <DescriptionPreview description={data.translation.de.description} />
          );
        }
      },
    },
    {
      field: 'amount',
      headerName: 'Price',
      style: { whiteSpace: 'nowrap' },
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon') {
          return (
            <Typography color="subdued" variant="text-3">
              {priceText(data?.amount)}
            </Typography>
          );
        }
      },
    },
    {
      field: 'limited',
      headerName: 'Limited',
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon') {
          return (
            <Typography color="subdued" variant="text-3">
              {data.limited || '∞'}
            </Typography>
          );
        }
      },
    },
    {
      field: 'vatRate',
      headerName: 'VAT',
      ...VAT_COLUMN,
      displayFunction: (data) => {
        if (data.id !== 'newOpt' && data.id !== 'newAddon') {
          return (
            <Typography color="subdued" variant="text-3">
              {vatColumnText(data)}
            </Typography>
          );
        }
      },
    },
  ];

  const handleEdit = (optionId: string, type: 'options' | 'addons') => {
    setOpenEditOptions({ isOpen: true, optId: optionId, type: type });
  };

  const handleAdd = (type: 'options' | 'addons') => {
    let newOptions: EventOption[];
    let newAddons: EventAddOn[];

    if (type === 'options') {
      if (options) {
        newOptions = [
          ...options,
          {
            id: 'newOpt',
            vatRate: '7.00',
            internalName: '',
            translation: {
              de: { title: '', description: '' },
              en: { title: '', description: '' },
            },
            type: 'perPerson',
          },
        ];
      } else {
        newOptions = [
          {
            id: 'newOpt',
            internalName: '',
            translation: {
              de: { title: '', description: '' },
              en: { title: '', description: '' },
            },
            vatRate: '19.00',
            type: 'perPerson',
          },
        ];
      }

      setOpenEditOptions({ isOpen: true, optId: 'newOpt', type: type });
      eventState &&
        setEventState({
          ...eventState,
          options: newOptions,
        });
    } else {
      if (optionalAddOns) {
        newAddons = [
          ...optionalAddOns,
          {
            id: 'newAddon',
            internalName: '',
            translation: {
              de: { title: '', description: '' },
              en: { title: '', description: '' },
            },
            type: 'perPerson',
            vatRate: '19.00',
          },
        ];
      } else {
        newAddons = [
          {
            id: 'newAddon',
            internalName: '',
            translation: {
              de: { title: '', description: '' },
              en: { title: '', description: '' },
            },
            vatRate: '19.00',
            type: 'perPerson',
          },
        ];
      }

      setOpenEditOptions({ isOpen: true, optId: 'newAddon', type: type });
      eventState &&
        setEventState({
          ...eventState,
          optionalAddOns: newAddons,
        });
    }
  };

  const handleDelete = (id: string, type: 'options' | 'addons') => {
    if (!eventState) return;
    let newOptions = eventState.options && [...eventState.options];
    let newAddons = eventState.optionalAddOns && [...eventState.optionalAddOns];

    if (type === 'options') {
      newOptions = newOptions?.filter((o) => o.id !== id);

      setEventState({ ...eventState, options: newOptions });
    } else {
      newAddons = newAddons?.filter((o) => o.id !== id);

      setEventState({ ...eventState, optionalAddOns: newAddons });
    }
  };

  const sortedOptions = useMemo(
    () =>
      options
        ?.sort((a, b) => (a.pos ?? 1000) - (b.pos ?? 1000))
        ?.map((s, i) => ({ ...s, pos: s.pos ?? i }))
        ?.sort((a, b) => a.pos - b.pos) ?? [],
    [options]
  );

  const handleSort = (id: string, dir: 'up' | 'down') => {
    let change = dir === 'up' ? -1 : 1;
    let newPos = Math.min(
      Math.max(sortedOptions.findIndex((o) => o.id === id) + change, 0),
      sortedOptions.length
    );

    setEventState((e) =>
      !!e
        ? {
            ...e,
            options:
              e.options
                ?.map((s, i) => ({
                  ...s,
                  pos: s.id === id ? newPos : s.pos ?? i,
                }))
                ?.sort(
                  (a, b) =>
                    a.pos * 10 +
                    (a.id === id ? change : 0) -
                    (b.pos * 10 + (b.id === id ? change : 0))
                )
                ?.map((s, i) => ({
                  ...s,
                  pos: i,
                })) ?? [],
          }
        : null
    );
  };

  const sortedAddons = useMemo(
    () =>
      optionalAddOns
        ?.sort((a, b) => (a.pos ?? 1000) - (b.pos ?? 1000))
        ?.map((s, i) => ({ ...s, pos: s.pos ?? i }))
        ?.sort((a, b) => a.pos - b.pos) ?? [],
    [optionalAddOns]
  );

  const handleSortAddons = (id: string, dir: 'up' | 'down') => {
    let change = dir === 'up' ? -1 : 1;
    let newPos = Math.min(
      Math.max(sortedAddons.findIndex((o) => o.id === id) + change, 0),
      sortedAddons.length
    );

    setEventState((e) =>
      !!e
        ? {
            ...e,
            optionalAddOns:
              e.optionalAddOns
                ?.map((s, i) => ({
                  ...s,
                  pos: s.id === id ? newPos : s.pos ?? i,
                }))
                ?.sort(
                  (a, b) =>
                    a.pos * 10 +
                    (a.id === id ? change : 0) -
                    (b.pos * 10 + (b.id === id ? change : 0))
                )
                ?.map((s, i) => ({
                  ...s,
                  pos: i,
                })) ?? [],
          }
        : null
    );
  };

  //OPTIONS ACTIONS
  const contextActions: ContextAction<EventOption>[] = [
    {
      icon: <Edit color="inherit" />,
      iconColor: 'var(--color-text-subdued)',
      onClick: (data) => {
        handleEdit(data.id, 'options');
      },
      showCondition: (data) => true,
      tooltipContent: 'Edit',
      tooltipContentTranslation: 'settings',
    },
    {
      icon: <Delete color="inherit" />,
      iconColor: 'var(--color-text-subdued)',
      onClick: (data) => handleDelete(data.id, 'options'),
      showCondition: (data) => true,
      tooltipContent: 'Delete',
      tooltipContentTranslation: 'settings',
    },
    {
      icon: <ArrowUpward color="inherit" />,
      iconColor: (data) =>
        (data?.pos ?? 0) > 0
          ? 'var(--color-text-subdued)'
          : 'var(--color-disabled)',
      onClick: (data) => handleSort(data.id, 'up'),
      showCondition: (data) => sortedOptions.length > 1,
      tooltipContent: 'Up',
      tooltipContentTranslation: 'settings',
    },
    {
      icon: <ArrowDownward color="inherit" />,
      iconColor: (data) =>
        (data?.pos ?? 0) !== sortedOptions.length - 1
          ? 'var(--color-text-subdued)'
          : 'var(--color-disabled)',
      onClick: (data) => handleSort(data.id, 'down'),
      showCondition: (data) => sortedOptions.length > 1,
      tooltipContent: 'Down',
      tooltipContentTranslation: 'settings',
    },
  ];

  // ADDONS ACTIONS
  const contextActionsAddons: ContextAction<EventAddOn>[] = [
    {
      icon: <Edit color="inherit" />,
      iconColor: 'var(--color-text-subdued)',
      onClick: (data) => {
        handleEdit(data.id, 'addons');
      },
      showCondition: (data) => true,
      tooltipContent: 'Edit',
      tooltipContentTranslation: 'settings',
    },
    {
      icon: <Delete color="inherit" />,
      iconColor: 'var(--color-text-subdued)',
      onClick: (data) => handleDelete(data.id, 'addons'),
      showCondition: (data) => true,
      tooltipContent: 'Delete',
      tooltipContentTranslation: 'settings',
    },
    {
      icon: <ArrowUpward color="inherit" />,
      iconColor: (data) =>
        (data?.pos ?? 0) > 0
          ? 'var(--color-text-subdued)'
          : 'var(--color-disabled)',
      onClick: (data) => handleSortAddons(data.id, 'up'),
      showCondition: () => sortedAddons.length > 1,
      tooltipContent: 'Up',
      tooltipContentTranslation: 'settings',
    },
    {
      icon: <ArrowDownward color="inherit" />,
      iconColor: (data) =>
        (data?.pos ?? 0) !== sortedAddons.length - 1
          ? 'var(--color-text-subdued)'
          : 'var(--color-disabled)',
      onClick: (data) => handleSortAddons(data.id, 'down'),
      showCondition: () => sortedAddons.length > 1,
      tooltipContent: 'Down',
      tooltipContentTranslation: 'settings',
    },
  ];

  if (!eventState) {
    return <></>;
  }

  return (
    <Box>
      <>
        <Box flex className="mg-bt-md space-between" style={{ marginTop: 24 }}>
          <Box width="auto">
            <Typography
              variant="h5"
              className="mg-bt-xs"
              translation="calendar"
            >
              Add Options
            </Typography>
            <Typography color="subdued" variant="text-3" translation="calendar">
              Add or edit your event options if needed. At least one option is
              needed
            </Typography>
          </Box>

          <Button
            style={{ width: '20%' }}
            endIcon={() => <Add style={{ marginLeft: 8 }} />}
            onClick={() => handleAdd('options')}
            translation="calendar"
          >
            New option
          </Button>
        </Box>

        <RadioButtonGroup
          style={{ display: 'flex' }}
          value={
            eventState.disallowMultipleOptions
              ? 'perPersonPerGroup'
              : eventState.optionPer ?? 'perPerson'
          }
          options={[
            {
              label: 'One Option Per Person',
              value: 'perPerson',
              translation: 'calendar',
            },
            {
              label: 'One Option Per Group',
              value: 'perGroup',
              translation: 'calendar',
            },
            {
              label: 'Price per Person, but one option per group',
              value: 'perPersonPerGroup',
              translation: 'calendar',
            },
          ]}
          onChange={(e: any) => {
            if (e.target.value === 'perPersonPerGroup') {
              setEventState({
                ...eventState,
                disallowMultipleOptions: true,
                optionPer: 'perPerson',
              });
            } else {
              setEventState({
                ...eventState,
                disallowMultipleOptions: false,
                optionPer: e.target.value,
              });
            }
          }}
          isRow
        />

        <div className="event-diner-table">
          <GTable
            headers={headersOptions}
            data={sortedOptions || []}
            contextActions={contextActions}
            borders
          />
        </div>
      </>

      {/* ADDONS */}
      <Box className="mg-bt-lg">
        <Box className="mg-bt-md space-between" flex style={{ marginTop: 24 }}>
          <Box width="auto">
            <Typography
              variant="h5"
              className="mg-bt-xs"
              translation="calendar"
            >
              Add optional Addons
            </Typography>
            <Typography color="subdued" variant="text-3" translation="calendar">
              Add or edit special add-ons if needed
            </Typography>
          </Box>

          <Button
            style={{ width: '20%' }}
            endIcon={() => <Add style={{ marginLeft: 8 }} />}
            onClick={() => handleAdd('addons')}
            translation="calendar"
          >
            New addon
          </Button>
        </Box>

        <div className="event-diner-table">
          <GTable
            headers={headersOptionalAddons}
            data={sortedAddons || []}
            contextActions={contextActionsAddons}
            borders
          />
        </div>
      </Box>

      {/* MODALS */}
      <EditOptionModal
        setEventState={setEventState}
        eventState={eventState}
        openEditOptions={openEditOptions}
        setOpenEditOptions={setOpenEditOptions}
      />
      <EditAddonModal
        setEventState={setEventState}
        eventState={eventState}
        openEditOptions={openEditOptions}
        setOpenEditOptions={setOpenEditOptions}
      />
    </Box>
  );
};

export default EventDiner;
